import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { erApiFeil } from '../api/feil.ts';
import { useArkiverKilde, useEndreKilde, useKilde, useKilder, useOpprettKilde, type MetricSource, type MetricSourceDetail, type SourceStatus } from '../api/useHypoteseKilder.ts';
import { useHypoteseConfig, type UpdateFrequency } from '../api/useHypotheses.ts';
import { useMe } from '../api/useMe.ts';
import { Knapp } from '../innboks/Detalj.tsx';
import { Toast, type ToastInnhold } from '../innboks/Toast.tsx';
import { Dialog } from '../skall/Dialog.tsx';
import { useTilgang } from '../skall/tilgang.ts';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { Ikon } from '../status/Ikon.tsx';
import { tokenVar } from '../status/status.ts';
import { Radiogruppe, Tekstknapp, Varsel } from './deler.tsx';
import { Skjelettrader } from './HListe.tsx';
import { frekvensOrd, kildestatus, lagringstid } from './kilder.ts';
import { fornavn, hvem, navnAv, stor } from './lesning.ts';
import { H_TIL_DEG } from './rommet.ts';
import { kortDatoAv } from './setning.ts';

/**
 * Kildekatalogen (H3.3, konseptsiden W9, W9b; avvik 9): en egen side under Innstillinger — liste
 * med detaljrute, slik Kjente feil er bygget. Toppen bærer tittel, telling, søk og «Legg til kilde»,
 * aldri en setning (DD-19: forklaringen står i tomtilstanden).
 *
 * Hvem gjør hva (tech-decisions 2026-10-29): eier legger inn, bytter eier og arkiverer; kildens eier
 * retter sin egen kilde; alle i rommet leser. Spørsmålene som venter, ser eier og kildens eier —
 * spørsmålet og hvem som spør, ALDRI utkastet (Kh15). En kilde arkiveres, slettes aldri.
 */

const BASE = '/h/innstillinger/kilder';
const OPPDATERES: Array<[UpdateFrequency, string]> = [
  ['daglig', 'Daglig'],
  ['ukentlig', 'Ukentlig'],
  ['maanedlig', 'Månedlig'],
  ['kvartalsvis', 'Kvartalsvis'],
  ['sjeldnere', 'Sjeldnere'],
];
const STATUSER: Array<[SourceStatus, string]> = [
  ['verifisert', 'Avklart'],
  ['uavklart_definisjon', 'Uklar'],
  ['maa_bygges', 'Må bygges'],
];

const hypoteserKort = (n: number): string => (n === 0 ? 'ingen ennå' : n === 1 ? '1 hypotese' : `${n} hypoteser`);

/** Radens meta-linje etter statusen (W9): «daglig · Samira Haddad · 90 dager». */
function radMeta(k: MetricSource): string[] {
  const deler = [...(k.status === 'maa_bygges' ? [] : [frekvensOrd(k.updateFrequency)]), navnAv(k.owner)];
  if (k.retentionDays !== undefined) deler.push(lagringstid(k.retentionDays));
  if (k.archivedAt) deler.push('arkivert');
  return deler;
}

export function Kildekatalog({ id }: { id: string | null }) {
  const smal = useSmal();
  const naviger = useNavigate();
  const tilgang = useTilgang();
  const me = useMe();
  const megId = me.data?.id;
  const eier = tilgang.eier;
  const [sok, setSok] = useState('');
  const [ny, setNy] = useState(false);
  const [toast, setToast] = useState<ToastInnhold | null>(null);
  const kilder = useKilder({ arkiverte: true, ...(sok.trim() ? { q: sok.trim() } : {}) });
  const alle = kilder.data?.items ?? [];
  const tilbake = eier ? { navn: 'Innstillinger', til: '/h/innstillinger' } : { navn: H_TIL_DEG.navn, til: H_TIL_DEG.sti };

  // Over 900 velges første rad når lista kommer (DD-57), som innboksen og Kjente feil.
  if (!smal && id === null && !ny && alle[0]) return <Navigate to={`${BASE}/${alle[0].id}`} replace />;

  const katalogTom = kilder.isSuccess && alle.length === 0 && !sok.trim();
  const aktive = alle.filter((k) => !k.archivedAt).length;

  const topp = (
    <div className="hb-liste-topp">
      <Link to={tilbake.til} className="panel-knapp-lenke k-tilbake">
        <Ikon navn="chevron-back-outline" />
        {tilbake.navn}
      </Link>
      <div className="hb-liste-tittel">
        <h1 className="panel-h1">Kilder</h1>
        {eier ? (
          <button type="button" className="panel-knapp-sekundaer k-ny" onClick={() => setNy(true)} data-testid="k-ny">
            <Ikon navn="add-outline" />
            Legg til kilde
          </button>
        ) : null}
      </div>
      {kilder.data ? (
        <p className="hb-liten k-telling">
          {aktive === 1 ? '1 kilde' : `${aktive} kilder`}
          {eier && kilder.data.openQuestionCount ? ` · ${kilder.data.openQuestionCount} spørsmål venter på en eier` : ''}
        </p>
      ) : null}
      <label className="panel-sok hb-sok">
        <Ikon navn="search-outline" />
        <input type="search" value={sok} onChange={(e) => setSok(e.target.value)} placeholder="Søk i kildene" aria-label="Søk i kildene" />
      </label>
    </div>
  );

  const liste = (
    <section className="hb-liste" aria-label="Kildene">
      <div className="hb-liste-rull">
        {topp}
        {kilder.isPending ? (
          <Skjelettrader etikett="Henter kildene" />
        ) : kilder.isError ? (
          <div className="hb-liste-melding">
            <Varsel handling={<Tekstknapp onClick={() => void kilder.refetch()}>Prøv igjen</Tekstknapp>}>Kildene kunne ikke hentes. Ingenting er endret.</Varsel>
          </div>
        ) : alle.length === 0 ? (
          <p className="hb-liste-melding hb-dempet">Ingen kilder treffer «{sok.trim()}».</p>
        ) : (
          <ul className="hb-rader">
            {alle.map((k) => (
              <KildeRad key={k.id} k={k} valgt={!smal && k.id === id} onVelg={() => {
                setNy(false);
                void naviger(`${BASE}/${k.id}`);
              }} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );

  const ferdig = (k: MetricSource, tekst: string) => {
    setNy(false);
    setToast({ tekst });
    void naviger(`${BASE}/${k.id}`);
  };

  const hoyre = ny ? (
    <section className="hb-detalj" aria-labelledby="k-ny-h">
      <div className="hb-detalj-rull">
        <div className="hb-detalj-innhold">
          <h2 id="k-ny-h" className="panel-h2">
            Ny kilde
          </h2>
          <KildeSkjema navn="Ny kilde" eier onAvbryt={() => setNy(false)} onLagret={(k) => ferdig(k, `${k.name} er lagt inn.`)} />
        </div>
      </div>
    </section>
  ) : id ? (
    <KildeDetalj id={id} megId={megId} eier={eier} smal={smal} tilbake={BASE} onToast={(t) => setToast({ tekst: t })} />
  ) : (
    <section className="hb-detalj hb-detalj-tom" aria-label="Ingen kilde valgt" />
  );

  const innhold =
    katalogTom && !ny ? (
      <div className="hb-tomflate">
        <div className="hb-tom k-tom" data-testid="k-tom">
          <Link to={tilbake.til} className="panel-knapp-lenke k-tilbake">
            <Ikon navn="chevron-back-outline" />
            {tilbake.navn}
          </Link>
          <Ikon navn="server-outline" className="k-tom-ikon" />
          <h1 className="panel-h1">Ingen kilder ennå</h1>
          {eier ? (
            <>
              <p className="hb-dempet">
                {kilder.data?.freeTextSuggestion
                  ? `Den første er den hypotesene alt bruker: ${kilder.data.freeTextSuggestion.hypothesisCount} ${kilder.data.freeTextSuggestion.hypothesisCount === 1 ? 'hypotese skriver' : 'hypoteser skriver'} «${kilder.data.freeTextSuggestion.sourceText}» som fritekst. Legg den inn med eier, så slipper neste innsender å gjette.`
                  : 'Legg inn kildene hypotesene måler fra, med eier, så slipper innsenderne å gjette.'}
              </p>
              <div>
                <button type="button" className="panel-knapp" onClick={() => setNy(true)}>
                  <Ikon navn="add-outline" />
                  Legg til kilde
                </button>
              </div>
            </>
          ) : (
            <p className="hb-dempet">Eieren hos {me.data?.tenantName ?? 'dere'} legger inn kildene. Til da skriver du kilden selv i skjemaet.</p>
          )}
        </div>
      </div>
    ) : smal ? (
      id || ny ? hoyre : liste
    ) : (
      <div className="hb-todelt">
        {liste}
        {hoyre}
      </div>
    );

  return (
    <>
      {innhold}
      {toast ? <Toast innhold={toast} onLukk={() => setToast(null)} /> : null}
    </>
  );
}

function KildeRad({ k, valgt, onVelg }: { k: MetricSource; valgt: boolean; onVelg: () => void }) {
  const venter = k.openQuestionCount ?? 0;
  return (
    <li>
      <button type="button" className={valgt ? 'hb-rad hb-rad-valgt k-rad' : 'hb-rad k-rad'} aria-current={valgt || undefined} onClick={onVelg} data-testid={`kilde-rad-${k.id}`}>
        <Ikon navn="server-outline" className="hb-rad-ikon" />
        <span className="hb-rad-tittel">{k.name}</span>
        {venter > 0 ? (
          <span className="hb-rad-hoyre k-venter">
            <Ikon navn="help-circle-outline" />
            {venter} spørsmål venter
          </span>
        ) : (
          <span className="hb-rad-hoyre">{hypoteserKort(k.hypothesisCount)}</span>
        )}
        <span className="hb-rad-meta">
          {/* Statusen først, halvfet i sin farge (avklart i tekstfargen, uklar og må bygges i warning). */}
          <b className="hb-rad-status" style={{ color: tokenVar(kildestatus(k.status).fg) }}>
            {kildestatus(k.status).kort}
          </b>
          {radMeta(k).map((d) => (
            <span key={d} className="hb-rad-ledd">
              <span aria-hidden="true" className="hb-prikk">
                ·
              </span>
              {d}
            </span>
          ))}
        </span>
      </button>
    </li>
  );
}

function KildeDetalj({ id, megId, eier, smal, tilbake, onToast }: { id: string; megId: string | undefined; eier: boolean; smal: boolean; tilbake: string; onToast: (t: string) => void }) {
  const detalj = useKilde(id);
  const [endrer, setEndrer] = useState(false);
  const [arkiverer, setArkiverer] = useState(false);
  const arkiver = useArkiverKilde();
  useEffect(() => setEndrer(false), [id]);

  if (detalj.isPending) {
    return (
      <section className="hb-detalj" aria-label="Henter kilden">
        <Skjelettrader etikett="Henter kilden" />
      </section>
    );
  }
  if (detalj.isError) {
    return (
      <section className="hb-detalj" aria-label="Fant ikke kilden">
        <div className="hb-detalj-innhold">
          <h2 className="panel-h2">Fant ikke kilden</h2>
          <p className="hb-dempet">Den finnes ikke hos dere. Velg en i lista.</p>
        </div>
      </section>
    );
  }
  const k: MetricSourceDetail = detalj.data;
  const st = kildestatus(k.status);
  const kanEndre = !k.archivedAt && (eier || k.owner.id === megId);
  const bruk = `${stor(k.hypothesisCount === 1 ? '1 hypotese bruker den' : `${k.hypothesisCount} hypoteser bruker den`)}${k.activeHypothesisCount > 0 ? ` — ${k.activeHypothesisCount} testes nå.` : '.'}`;

  return (
    <section className="hb-detalj" aria-labelledby="k-tittel" data-testid="k-detalj">
      {smal ? (
        <header className="h-stripe">
          <Link to={tilbake} className="panel-stripe-tilbake" aria-label="Tilbake til Kilder">
            <Ikon navn="chevron-back-outline" />
          </Link>
          <span className="h-stripe-tittel">Kilde</span>
        </header>
      ) : null}
      <div className="hb-detalj-rull">
        <div className="hb-detalj-innhold">
          <div className="hb-spor-topp">
            <span>
              Kilde · lagt inn av {hvem(k.createdBy, megId, 'deg')} {kortDatoAv(k.createdAt)}
              {k.verifiedBy && k.verifiedAt ? ` · verifisert av ${hvem(k.verifiedBy, megId, 'deg')} ${kortDatoAv(k.verifiedAt)}` : ''}
            </span>
            {kanEndre && !endrer ? (
              <span className="hb-spor-topp-hoyre">
                <button type="button" className="panel-knapp-lenke" onClick={() => setEndrer(true)}>
                  <Ikon navn="create-outline" />
                  Endre
                </button>
              </span>
            ) : null}
          </div>
          <h2 id="k-tittel" className="hb-tittel">
            {k.name}
          </h2>
          {k.archivedAt ? (
            <Varsel tone="info" rolle="status">
              Arkivert {kortDatoAv(k.archivedAt)}. Den kan ikke velges i skjemaet; hypotesene som bruker den, beholder den.
            </Varsel>
          ) : null}
          {endrer ? (
            <KildeSkjema
              navn={`Endre ${k.name}`}
              eier={eier}
              kilde={k}
              onAvbryt={() => setEndrer(false)}
              onLagret={(ny) => {
                setEndrer(false);
                onToast(`${ny.name} er lagret.`);
              }}
            />
          ) : (
            <>
              <p className="k-status">
                <span className="k-status-merke" style={{ color: tokenVar(st.fg) }}>
                  <Ikon navn={st.ikon} />
                  <b>{st.etikett}</b>
                </span>
                <span className="hb-dempet">{bruk}</span>
              </p>
              <dl className="k-fakta">
                <div className="hb-fakta-rad">
                  <dt>Måler</dt>
                  <dd>{k.measures}</dd>
                </div>
                <div className="hb-fakta-rad">
                  <dt>Eier</dt>
                  <dd>{hvem(k.owner, megId, 'Du')}</dd>
                </div>
                <div className="hb-fakta-rad">
                  <dt>Oppdateres</dt>
                  <dd>{stor(frekvensOrd(k.updateFrequency))}</dd>
                </div>
                <div className="hb-fakta-rad">
                  <dt>Data beholdes</dt>
                  <dd>{k.retentionDays !== undefined ? `${lagringstid(k.retentionDays)} — en baseline må tas før den er borte` : 'Ikke oppgitt'}</dd>
                </div>
              </dl>
            </>
          )}
          {(k.openQuestions ?? []).map((q) => (
            <div key={q.id} className="hb-kort k-sporsmaal" data-testid={`kilde-sporsmaal-${q.id}`}>
              <div className="hb-kort-hode">
                <b>
                  <Ikon navn="help-circle-outline" /> {navnAv(q.askedBy)} spør · venter på {hvem(q.answerer, megId, 'deg')}
                </b>
                <span className="hb-liten">{kortDatoAv(q.askedAt)}</span>
              </div>
              <p className="hb-tekst">{q.question}</p>
              {q.answerer.id === megId ? (
                <p>
                  <Link to={`/h/til-deg/sporsmal/${q.id}`} className="panel-knapp-lenke">
                    Svar på spørsmålet
                  </Link>
                </p>
              ) : (
                <p className="hb-dempet">{`${stor(fornavn(q.answerer))} ser utkastet til ${fornavn(q.askedBy)} til det er besvart. Du ser bare spørsmålet.`}</p>
              )}
            </div>
          ))}
          <ol className="hb-logg" aria-label="Loggen">
            {k.verifiedBy && k.verifiedAt ? (
              <li className="hb-logg-rad">
                <span className="hb-fet">
                  <Ikon navn="shield-checkmark-outline" /> {stor(hvem(k.verifiedBy, megId, 'Du'))} bekreftet definisjonen
                </span>
                <span className="hb-liten">{kortDatoAv(k.verifiedAt)}</span>
              </li>
            ) : null}
            <li className="hb-logg-rad">
              <span className="hb-fet">
                <Ikon navn="add-outline" /> {stor(hvem(k.createdBy, megId, 'Du'))} la inn kilden
              </span>
              <span className="hb-liten">{kortDatoAv(k.createdAt)}</span>
            </li>
          </ol>
          {eier && !k.archivedAt && !endrer ? (
            <div>
              <button type="button" className="panel-knapp-lenke hb-noeytral" onClick={() => setArkiverer(true)}>
                Arkiver kilden …
              </button>
            </div>
          ) : null}
        </div>
      </div>
      {arkiverer ? (
        // En bekreftelse fordi den rammer andre: spørsmålene som venter, lukkes (ux-flow-architect).
        <Dialog tittelId="k-arkiver-h" onLukk={() => setArkiverer(false)} testid="k-arkiver-dialog">
          <h2 id="k-arkiver-h" className="panel-h2">
            Arkiver {k.name}?
          </h2>
          <p className="panel-tekst">
            {stor(k.hypothesisCount === 1 ? '1 hypotese bruker den' : `${k.hypothesisCount} hypoteser bruker den`)} og beholder den. Den kan ikke velges i skjemaet etterpå, og spørsmål som venter, lukkes. Ingenting slettes.
          </p>
          {arkiver.isError ? <Varsel>Fikk ikke arkivert. Prøv igjen.</Varsel> : null}
          <div className="panel-handlinger">
            <Knapp
              label="Arkiver kilden"
              primaer
              onClick={() =>
                arkiver.mutate(k.id, {
                  onSuccess: () => {
                    setArkiverer(false);
                    onToast(`${k.name} er arkivert.`);
                  },
                })
              }
            />
            <Knapp label="Avbryt" kbd="Esc" fokusFørst onClick={() => setArkiverer(false)} />
          </div>
        </Dialog>
      ) : null}
    </section>
  );
}

/**
 * «Legg til kilde» (fem felt, W9b) og «Endre» (de samme, pluss statusen). Eieren velges fra rommets
 * personer — bare eier kan bytte den; kildens eier ser den ikke som et felt.
 */
function KildeSkjema({ navn, eier, kilde, onAvbryt, onLagret }: { navn: string; eier: boolean; kilde?: MetricSource; onAvbryt: () => void; onLagret: (k: MetricSource) => void }) {
  const config = useHypoteseConfig();
  const opprett = useOpprettKilde();
  const endre = useEndreKilde();
  const [n, setN] = useState(kilde?.name ?? '');
  const [maaler, setMaaler] = useState(kilde?.measures ?? '');
  const [eierId, setEierId] = useState(kilde?.owner.id ?? '');
  const [frekvens, setFrekvens] = useState<UpdateFrequency | undefined>(kilde?.updateFrequency);
  const [dager, setDager] = useState(kilde?.retentionDays !== undefined ? String(kilde.retentionDays) : '');
  const [status, setStatus] = useState<SourceStatus | undefined>(kilde?.status);
  const [feil, setFeil] = useState<string | null>(null);
  const min = config.data?.minFieldLength ?? 10;
  const personer = useMemo(() => [...(config.data?.members ?? [])].sort((a, b) => navnAv(a).localeCompare(navnAv(b), 'nb')), [config.data]);

  const grunn = n.trim().length < 3 ? 'Navnet må ha minst 3 tegn.' : maaler.trim().length < min ? `«Hva den måler» må ha minst ${min} tegn.` : eier && !eierId ? 'Velg en eier.' : !frekvens ? 'Velg hvor ofte den oppdateres.' : dager.trim() && !/^\d+$/.test(dager.trim()) ? 'Dagene må være et helt tall.' : null;
  const lagrer = opprett.isPending || endre.isPending;

  const lagre = () => {
    if (grunn || lagrer) return;
    setFeil(null);
    const retention = dager.trim() ? Number(dager.trim()) : undefined;
    const vedFeil = (e: unknown) => setFeil(erApiFeil(e) ? e.brukertekst : 'Fikk ikke lagret. Prøv igjen.');
    if (!kilde) {
      opprett.mutate(
        { name: n.trim(), measures: maaler.trim(), ownerId: eierId, updateFrequency: frekvens!, ...(retention !== undefined ? { retentionDays: retention } : {}) },
        { onSuccess: onLagret, onError: vedFeil },
      );
      return;
    }
    const body: Record<string, unknown> = {};
    if (n.trim() !== kilde.name) body.name = n.trim();
    if (maaler.trim() !== kilde.measures) body.measures = maaler.trim();
    if (eier && eierId !== kilde.owner.id) body.ownerId = eierId;
    if (frekvens !== kilde.updateFrequency) body.updateFrequency = frekvens;
    if (retention !== kilde.retentionDays) body.retentionDays = retention ?? null;
    if (status && status !== kilde.status) body.status = status;
    endre.mutate({ id: kilde.id, body }, { onSuccess: onLagret, onError: vedFeil });
  };
  const send = (e: FormEvent) => {
    e.preventDefault();
    lagre();
  };

  return (
    <form aria-label={navn} className="k-skjema" onSubmit={send} noValidate>
      <label className="panel-felt">
        <span>Navn</span>
        <input type="text" value={n} maxLength={80} onChange={(e) => setN(e.target.value)} />
      </label>
      <label className="panel-felt">
        <span>Hva den måler</span>
        <textarea value={maaler} rows={3} maxLength={500} onChange={(e) => setMaaler(e.target.value)} />
      </label>
      {eier ? (
        <label className="panel-felt">
          <span>Eier</span>
          <select value={eierId} onChange={(e) => setEierId(e.target.value)}>
            <option value="">Velg eier</option>
            {personer.map((p) => (
              <option key={p.id} value={p.id}>
                {navnAv(p)}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <div className="panel-felt">
        <span id="k-frekvens-h">Oppdateres</span>
        <Radiogruppe etikettId="k-frekvens-h" valg={OPPDATERES} aktiv={frekvens} onVelg={setFrekvens} />
      </div>
      <label className="panel-felt k-dager">
        <span>Data beholdes (dager) · valgfritt</span>
        <input type="number" min={1} max={36500} inputMode="numeric" value={dager} onChange={(e) => setDager(e.target.value)} />
      </label>
      {kilde ? (
        <div className="panel-felt">
          <span id="k-status-h">Definisjonen</span>
          <Radiogruppe etikettId="k-status-h" valg={STATUSER} aktiv={status} onVelg={setStatus} />
        </div>
      ) : null}
      {feil ? <Varsel>{feil}</Varsel> : null}
      <div className="panel-handlinger k-skjema-knapper">
        <Knapp label="Avbryt" lenke onClick={onAvbryt} />
        <Knapp label={kilde ? 'Lagre' : 'Legg til'} primaer grunn={grunn} onClick={lagre} />
      </div>
    </form>
  );
}
